import { ReactComponent as ArrowIcon } from "../../assets/icons/chevron_down_black.svg";
import { ReactComponent as DisabledArrowIcon } from "../../assets/icons/chevron_down_grey.svg";
import dropdownStyles from "../../styles/UI/accordion.module.css";
import { useEffect, useId, useRef, useState } from "react";
import inputStyles from "../../styles/UI/inputFields.module.css";
import AccordionList from "./AccordionList";
import labels from "../../utils/labels";

export type AccordionVariant = "PRIMARY" | "SECONDARY";

const Accordion = <T,>({
  onChange,
  variant,
  options,
  labelKey,
  defaultText,
  displayOption,
  label,
  classes,
  buttonClasses,
  leadingElement,
  disabled,
  helperText,
  error,
}: {
  onChange: (option: T) => void;
  variant: AccordionVariant;
  options: T[];
  labelKey?: keyof T;
  defaultText: string;
  displayOption?: number; // if user wants to show a partiluar option by index by default
  label?: string;
  classes?: string;
  buttonClasses?: string;
  leadingElement?: (option: T, isSelected: boolean) => React.ReactNode;
  disabled?: boolean;
  helperText?: string;
  error?: boolean;
}) => {
  const [open, toggleOpen] = useState<boolean>();
  const [selectedOption, setSelectedOption] = useState<T>();
  const [displayText, setDisplayText] = useState<string>();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listId = useId();

  const closeAndFocusButton = () => {
    toggleOpen(false);
    buttonRef.current?.focus();
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.relatedTarget as Node)
    ) {
      toggleOpen(false);
    }
  };

  const handleTriggerKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
  ): void => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      toggleOpen(true);
    }
  };

  useEffect(() => {
    if (selectedOption !== undefined && selectedOption !== null) {
      setDisplayText(
        labelKey
          ? (selectedOption[labelKey] as string)
          : (selectedOption as string),
      );
    } else {
      setDisplayText(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  useEffect(() => {
    if (typeof displayOption === "number") {
      const option = options[displayOption];
      if (option !== undefined) {
        setSelectedOption(option);
        onChange(option);
      }
    }

    const handleClickOutside = (event: PointerEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        toggleOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleClickOutside);
    return () =>
      document.removeEventListener("pointerdown", handleClickOutside);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`relative flex flex-col gap-[var(--spacing-xs)] ${classes ?? ""}`}
      ref={dropdownRef}
      onBlur={handleBlur}
    >
      {variant === "PRIMARY" && (
        <span className={`pl-2 ${inputStyles.label}`}>{label}</span>
      )}
      {/* Preserve popup focus during a Safari mouse click until the trigger toggles. */}
      <button
        ref={buttonRef}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={!!open}
        aria-controls={listId}
        aria-label={label ?? defaultText}
        onMouseDown={(event): void => event.preventDefault()}
        className={`
          ${dropdownStyles.accordion}
          ${variant === "PRIMARY" ? dropdownStyles.accordion__primary : ""}
          ${disabled ? dropdownStyles.accordion__disabled : ""}
          ${error ? dropdownStyles.accordion__error : ""}
          ${buttonClasses ?? ""}
        `}
        onClick={() => {
          toggleOpen((prevState) => !prevState);
        }}
        onKeyDown={handleTriggerKeyDown}
      >
        <div className="flex gap-[var(--spacing-sm)] justify-start min-w-0 flex-1 overflow-hidden">
          {selectedOption !== undefined &&
            selectedOption !== null &&
            leadingElement &&
            leadingElement(selectedOption, false)}
          <p
            className={`text-left truncate ${displayText !== undefined ? "" : "text-[var(--col-text-secondary)]"}`}
            title={displayText !== undefined ? displayText : defaultText}
          >
            {displayText !== undefined ? displayText : defaultText}
          </p>
        </div>
        {disabled ? (
          <DisabledArrowIcon width={16} height={16} className="shrink-0" />
        ) : (
          <ArrowIcon width={16} height={16} className="shrink-0" />
        )}
      </button>
      {helperText && (
        <span className="text-[var(--col-text-secondary)]">{helperText}</span>
      )}
      {open && (
        <AccordionList
          id={listId}
          options={options}
          labelKey={labelKey}
          selectedOption={selectedOption}
          leadingElement={leadingElement}
          label={labels.chooseOption}
          onEscape={closeAndFocusButton}
          onSelect={(option) => {
            setSelectedOption(option);
            onChange(option);
            closeAndFocusButton();
          }}
        />
      )}
    </div>
  );
};

export default Accordion;
